// `full-page-dialog.component.ng1.ts` — the chrome around the six screens the extension opens
// as a dialog over the page the recruiter is on (plan §7 phase 5).
//
// It is six lines of template, and both of them matter:
//
//     <div class="closing-overlay" ng-click="ctrl.onOverlayClicked()"></div>
//     <ui-view class="chrome-extension-full-app" ng-class="ctrl.stateClass"></ui-view>
//
// ⚠️ THE OVERLAY IS THE ONLY WAY OUT OF THESE SCREENS. None of them draws a close button:
// clicking the overlay posts `CloseDialog` to the parent frame, which is the ATS page's own
// content script, and the iframe goes away. A port that renders the screens without it looks
// identical and traps the user — the one interaction on the screen is the one you cannot see.
// The message itself is the shell's (Phase 6.3); what lands here is the element, its class and
// its handler, so the click has somewhere to go the moment the channel exists.
//
// ⚠️ AND THE STATE CLASS IS DERIVED FROM THE STATE NAME, dots to dashes plus a suffix:
// `chrome-extension.full-page-dialog.pending` becomes
// `chrome-extension-full-page-dialog-pending-full-page-dialog`. Nothing in the stylesheets
// hangs off it today, which is exactly why it is easy to leave out and worth putting in — it is
// the hook every one of these screens' rules would use, and upstream recomputes it on every
// transition rather than setting it once.
import type { ReactElement, ReactNode } from 'react'

/** `getStateClass` — the state name with its dots flattened, suffixed. */
export function dialogStateClass(stateName: string): string {
  return `${stateName.replace(/\./g, '-')}-full-page-dialog`
}

export interface FullPageDialogProps {
  /** The ui-router state name of the screen inside, from `routes.json`. */
  stateName?: string
  /** `onOverlayClicked` — posts `CloseDialog` to the invoking frame. */
  onClose?: () => void
  children: ReactNode
}

export function FullPageDialog({ stateName, onClose, children }: FullPageDialogProps): ReactElement {
  return (
    <>
      <div
        className="closing-overlay"
        role="button"
        tabIndex={0}
        aria-label="Close"
        onClick={() => {
          onClose?.()
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') onClose?.()
        }}
      />
      <div className={['chrome-extension-full-app', stateName === undefined ? '' : dialogStateClass(stateName)]
        .filter((name) => name.length > 0)
        .join(' ')}
      >
        {children}
      </div>
    </>
  )
}
